<template>
    <b-sidebar
        id="room-form-sidebar"
        :visible="isRoomFormSidebarActive"
        bg-variant="white"
        sidebar-class="sidebar-lg"
        shadow
        backdrop
        no-header
        right
        @shown="getData(roomData)"
        @hidden="resetForm"
        @change="(val) => $emit('update:is-room-form-sidebar-active', val)"
    >
        <template #default="{ hide }">
            <div
                class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
            >
                <h5 class="mb-0">Room Form</h5>
                <feather-icon
                    class="ml-1 cursor-pointer"
                    icon="XIcon"
                    size="16"
                    @click="close(hide)"
                />
            </div>
            <validation-observer v-slot="{ validate }" ref="refFormObserver">
                <b-form
                    class="p-2"
                    @submit.prevent="onSubmit(true)"
                    @reset.prevent="resetForm"
                >
                    <b-tabs v-model="tabIndex">
                        <b-tab title="Content">
                            <b-tabs content-class="mt-1" v-if="langs.length > 0">
                                <b-tab
                                    v-for="(lang, key) in langs"
                                    :key="'tab-' + key"
                                    :title="lang.name"
                                >
                                    <validation-provider
                                        v-slot="{errors, validate}"
                                        :name="'room_name_' + lang.code"
                                        rules="required"
                                    >
                                        <b-form-group
                                            label="Room Name"
                                            :label-for="'name_' + lang.code"
                                        >
                                            <b-form-input
                                                :id="'name_' + lang.code"
                                                v-model="Data.title[lang.code]"
                                                autofocus
                                                :state="errors[0] ? false : null"
                                                trim
                                                :placeholder="'name (' + lang.code + ')'"
                                                @input="(value) => { validate(); changeTitle(lang.code, value); }"
                                            />
                                            <b-form-invalid-feedback>
                                                {{ errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider
                                        #default="validationContext"
                                        :name="'sub_title'"
                                    >
                                        <b-form-group
                                            label="Sub Title"
                                            :label-for="'sub_title'"
                                        >
                                            <b-form-input
                                                :id="'sub_title'"
                                                v-model="Data.sub_title[lang.code]"
                                                autofocus
                                                :state="getValidationState(validationContext)"
                                                trim
                                                :placeholder="`sub title (${lang.code})`"
                                            />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider
                                        #default="validationContext"
                                        :name="'description_' + lang.code"
                                        rules="min:0"
                                    >
                                        <b-form-group
                                            label="Description"
                                            :label-for="'description_' + lang.code"
                                        >
                                            <quill-editor
                                                :options="editorOptions"
                                                :id="'description_' + lang.code"
                                                v-model="Data.description[lang.code]"
                                            />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider
                                        #default="validationContext"
                                        :name="'short_description'"
                                    >
                                        <b-form-group
                                            label="Short Description"
                                            :label-for="'short_description'"
                                        >
                                            <b-form-input
                                                :id="'short_description'"
                                                v-model="Data.short_description[lang.code]"
                                                autofocus
                                                :state="getValidationState(validationContext)"
                                                trim
                                                :placeholder="`short description (${lang.code})`"
                                            />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider
                                        #default="validationContext"
                                        :name="'slug' + lang.code"
                                        rules="required"
                                    >
                                        <b-form-group
                                            label="Slug"
                                            :label-for="'slug' + lang.code"
                                        >
                                            <b-form-input
                                                :id="'slug' + lang.code"
                                                v-model="Data.slug[lang.code]"
                                                autofocus
                                                :state="getValidationState(validationContext)"
                                                trim
                                                :placeholder="'Slug (' + lang.code + ')'"
                                            />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <div v-if="Data.features.length">{{  `Room Features`  }}</div>
                                    <div v-for="(item, index) in Data.features" :key="index">
                                        <b-row align-v="center">
                                            <b-col cols="1" align-self="center">
                                                <b-img
                                                    :src="`${item.feature? item.feature.icon_path: item.icon_path}`"
                                                    height="75"
                                                    width="75"
                                                    class="rounded mr-2 mb-1 mb-md-0"
                                                />
                                            </b-col>
                                            <b-col cols="1">
                                                {{ item.name[lang.code] }}
                                            </b-col>
                                            <b-col>
                                                <validation-provider
                                                    #default="validationContext"
                                                    :name="'value_' + lang.code"
                                                >
                                                    <b-form-group
                                                        label="Value"
                                                        :label-for="'value__' + lang.code"
                                                    >
                                                        <b-form-input
                                                            :id="'value_' + lang.code"
                                                            v-model="item.value[lang.code].value"
                                                            autofocus
                                                            :state="getValidationState(validationContext)"
                                                            trim
                                                            :placeholder="'value (' + lang.code + ')'"
                                                        />
                                                        <b-form-invalid-feedback>
                                                            {{ validationContext.errors[0] }}
                                                        </b-form-invalid-feedback>
                                                    </b-form-group>
                                                </validation-provider>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-tab>
                            </b-tabs>
                            <hr />
                            <validation-provider
                                #default="validationContext"
                                :name="'price'"
                                rules="required"
                            >
                                <b-form-group
                                    label="Price"
                                    :label-for="'price'"
                                >
                                    <b-form-input
                                        :id="'price'"
                                        v-model="Data.price"
                                        autofocus
                                        :state="getValidationState(validationContext)"
                                        trim
                                        :placeholder="'price'"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider
                                #default="validationContext"
                                name="amenity"
                                rules="min:0"
                            >
                                <b-form-group label="Amenity" label-for="amenity_id">
                                    <v-select
                                        id="amenity_id"
                                        v-model="Data.amenities"
                                        multiple
                                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                        label="title"
                                        :options="
                                            amenitiesData.map((g) => ({
                                                title: g.title[currentLocale],
                                                id: g.id,
                                            }))
                                        "
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider
                                #default="validationContext"
                                :name="'url'"
                                rules="required"
                            >
                                <b-form-group
                                    label="URL"
                                    :label-for="'url'"
                                >
                                    <b-form-input
                                        :id="'url'"
                                        v-model="Data.url"
                                        autofocus
                                        :state="getValidationState(validationContext)"
                                        trim
                                        :placeholder="'url'"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <div>
                                <b-button
                                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                    variant="outline-primary"
                                    class="mr-2"
                                    @click="uploadImage('image')"
                                >
                                    Upload Image
                                </b-button>
                                <file-lists
                                    :is-modal-file-lists-show.sync="isModalFileListsShow"
                                    :uploadType="uploadType"
                                    @refesh="refesh"
                                />
                            </div>
                            <br/>
                            <div>
                                <validation-provider
                                    #default="validationContext"
                                    name="cover_image_id"
                                >
                                    <b-form-group label="Cover Image" label-for="image">
                                        <div id="cover_image_id">
                                            <treeselect
                                                @input="eventSelected"
                                                v-model="model" 
                                                placeholder="Choose Folder"
                                                :reduce="(option) => option.id"
                                                label="title"
                                                :options="folderOptions"
                                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                                noChildrenText
                                            />
                                        </div>
                                        <b-form-invalid-feedback>
                                            {{ validationContext.errors[0] }}
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>
    
                                <div>
                                    <b-container align-h="center">
                                        <b-row>
                                            <b-col 
                                                cols="3"
                                                v-for="(image, index) in images"
                                                :key="index"
                                                class="pt-2"
                                            >
                                            <img 
                                                @click="eventSelectedCoverImg(image)"
                                                :src="image.path"
                                                width="300"
                                                height="200"
                                                :class="selectedCoverImage==null
                                                    ? '' 
                                                    : selectedCoverImage.id == image.id
                                                        ? 'selected-img'
                                                        : 'disable-img'
                                                "
                                            >
                                            <br/>
                                        </b-col>
                                        </b-row>
                                    </b-container>
                                </div>
                            </div>
                            <div v-if="selectedCoverImage!==null">
                                <hr/>
                                <div>Choose Guide Images</div>
                                <b-container align-h="center">
                                    <b-row>
                                        <b-col 
                                            cols="3"
                                            v-for="(image, index) in images.filter(image => image.id !== selectedCoverImage.id)"
                                            :key="index"
                                            class="pt-2"
                                        >
                                        <img 
                                            @click="eventImgSelected(image)"
                                            :src="image.path"
                                            width="300"
                                            height="200"
                                            :class="selectedImages.some(v => v.id === image.id)? 'selected-img' : ''"
                                        >
                                    </b-col>
                                    <hr/>
                                    </b-row>
                                </b-container>
                                <br/>
                            </div>
                            <br/>
                            <validation-provider
                                #default="validationContext"
                                name="IsActive"
                                rules="min:0"
                            >
                                <b-form-group label="Is Active" label-for="IsActive">
                                <b-form-checkbox
                                    id="isactive"
                                    checked="true"
                                    class="custom-control-success"
                                    v-model="Data.is_active"
                                    switch
                                />
                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </b-tab>
                        <b-tab title="SEO">
                            <validation-provider
                              #default="validationContext"
                              name="meta_title"
                              rules="min:0"
                            >
                              <b-form-group label="Meta Title" label-for="meta_title">
                                <b-form-input
                                  id="meta_title"
                                  v-model="Data.meta_title"
                                  autofocus
                                  :state="getValidationState(validationContext)"
                                  trim
                                  placeholder="Meta Title"
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                            <validation-provider
                              #default="validationContext"
                              name="meta_description"
                              rules="min:0"
                              
                            >
                              <b-form-group
                                label="Meta Description"
                                label-for="meta_description"
                              >
                                <b-form-input
                                  id="meta_description"
                                  v-model="Data.meta_description"
                                  autofocus
                                  :state="getValidationState(validationContext)"
                                  trim
                                  placeholder="Meta Description"
                                />
              
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                            <validation-provider
                                #default="validationContext"
                                name="meta_keywords"
                                rules="min:0"
                            >
                                <b-form-group
                                label="Meta Keywords"
                                label-for="meta_keywords"
                                >
                                    <b-form-input
                                        id="meta_keywords"
                                        v-model="Data.meta_keyword"
                                        autofocus
                                        :state="getValidationState(validationContext)"
                                        trim
                                        placeholder="Meta Keywords"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                          </b-tab>
                    </b-tabs>
                    <div class="d-flex mt-2">
                        <b-button
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="outline-success"
                          class="mr-2"
                          type="submit"
                        >
                          Save
                        </b-button>
                        <b-button
                          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                          type="button"
                          variant="outline-danger"
                          @click="close(hide)"
                        >
                          Cancel
                        </b-button>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </b-sidebar>
</template>
    
<script>
    import { $themeConfig } from '@themeConfig'
    import {
        BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BFormInvalidFeedback,
        BButton, BTabs, BTab, BCard, BCardText, BFormRadio, BFormRadioGroup, BFormCheckboxGroup, BContainer,
        BRow, BCol, BImg,
    } from "bootstrap-vue";
    import { required, alphaNum, email, password,confirmed } from '@validations'
    import { ValidationProvider, ValidationObserver } from "vee-validate";
    import vSelect from "vue-select";
    import store from "@/store";
    import languagesStoreModule from "@/views/languages/languagesStoreModule";
    import folderStoreModule from '../../files/folderStoreModule'
    import formValidation from "@core/comp-functions/forms/form-validation";
    import Ripple from "vue-ripple-directive";
    import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
    import { useToast } from "vue-toastification/composition";
    import { ref, onUnmounted } from "@vue/composition-api";
    import { slugify } from '@/@fake-db/utils'
    import { quillEditor } from "vue-quill-editor";
    import Quill from 'quill'
    const sizeStyle = Quill.import('attributors/style/size')
    sizeStyle.whitelist = ['6px', '8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px']
    Quill.register(sizeStyle, true)
    import "quill/dist/quill.snow.css";
    import "quill/dist/quill.core.css";
    import "quill/dist/quill.bubble.css";
    import Treeselect from '@riophae/vue-treeselect'
    import '@riophae/vue-treeselect/dist/vue-treeselect.css'
    import FileLists from '../../uploadStorage/FileLists.vue'
    
    export default {
        components: {
            BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BFormInvalidFeedback,
            BButton, BTabs, BTab, BCard, BCardText, vSelect, ValidationProvider,
            ValidationObserver, BFormRadio, quillEditor, Treeselect, BFormRadioGroup, BFormCheckboxGroup,
            BContainer, BRow, BCol, FileLists, BImg,
        },
        directives: {
            Ripple,
        },
        model: {
            prop: "isRoomFormSidebarActive",
            event: "update:is-room-form-sidebar-active",
        },
        props: {
            isRoomFormSidebarActive: {
                type: Boolean,
                required: true,
            },
            roomData: {
                type: Object,
                required: false,
            },
        },
        computed: {
            currentLocale(){
                return this.$i18n.locale;
            },
            folderOptions(){
                return this.folders.map((folder) => this.formatFolder(folder))
            },
            editorOptions (){
                return $themeConfig.snowOption
            }
        },
        methods: {
            formatFolder(folder){
                return{
                    id: folder.id,
                    label: folder.title,
                    children: folder.subFolders.length? folder.subFolders.map(this.formatFolder): undefined
                }
            },
        },
        setup(props, { emit }){
            const {  appRef } = $themeConfig.app
            const toast = useToast();
            const folders = ref([])
            const model = ref(null)
            const images = ref([])
            const parent = ref(0);
            const selectedCoverImage = ref("")
            const selectedRoomImages = ref([])
            const userStorage = JSON.parse(localStorage.getItem('userData'));
            const getLocalSite = JSON.parse(localStorage.getItem('site'))
            const selectedImages = ref([])
            const isModalFileListsShow = ref(false)
            const formObserver = ref(null);
    
            const LANGUAGES_STORE_MODULE_NAME = "app-languages";
            if (!store.hasModule(LANGUAGES_STORE_MODULE_NAME))
            store.registerModule(LANGUAGES_STORE_MODULE_NAME, languagesStoreModule);
            onUnmounted(() => {
                if (store.hasModule(LANGUAGES_STORE_MODULE_NAME))
                store.unregisterModule(LANGUAGES_STORE_MODULE_NAME);
            });
    
            const FOLDER_STORE_MODULE_NAME = 'files-folder'
            if (!store.hasModule(FOLDER_STORE_MODULE_NAME)) store.registerModule(FOLDER_STORE_MODULE_NAME, folderStoreModule)
            onUnmounted(() => {
                if (store.hasModule(FOLDER_STORE_MODULE_NAME)) store.unregisterModule(FOLDER_STORE_MODULE_NAME)
            });
            const blankData = {
                meta_title: '',
                meta_description: '',
                meta_keyword: '',
                title: {},
                sub_title: {},
                description: {},
                short_description: {},
                slug: {},
                price: 0,
                cover_image: [],
                files: [],
                features: [],
                url: null,
                is_active: true,
                ordering_count: 0,
                amenities: [],
                sites_id: getLocalSite.id,
                created_by: userStorage.id,
            };
    
            const Data = ref(blankData);
            const oldValue  =  ref(null)
            const tabIndex = ref(0);
            const langs = ref([]);
            const amenitiesData = ref([]);
            const uploadType = ref('')
    
            const resetData = () => {
                Data.value = blankData;
            };
    
            const changeTitle  = (langCode, value) => {
                Data.value.slug[langCode] =  slugify(value)
            }
    
            store.dispatch('app-room/getAmenities', {
                sites_id: getLocalSite.id
            })
            .then(response => {
                amenitiesData.value = response.data.data
            })
    
            const uploadImage = (type) => {
                uploadType.value = type
                isModalFileListsShow.value = true
            }
    
            const eventSelected = (id) => {
                store.dispatch('files-folder/getFolderById', id)
                    .then(response => {
                        const res = response.data.data
                        images.value = res[0].images
    
                        const existingFolderId = Data?.value?.files[0]?.folder_id
    
                        if(model.value != existingFolderId){
                            selectedCoverImage.value = null
                            selectedImages.value = []
                        }
                })
            }
    
            const eventSelectedCoverImg = (data) => {
                const coverImg = selectedCoverImage.value
    
                if(coverImg == null){
                    selectedCoverImage.value = data
                    return
                }
    
                if(coverImg?.id == data.id){
                    selectedCoverImage.value = null
                    return
                }
            }
    
            const eventImgSelected = (data) => {
                const index = selectedImages.value.findIndex(item => item.id === data.id);
                index === -1 ? selectedImages.value.push(data) : selectedImages.value.splice(index, 1);
            }
    
            const close = (hidden) => {
                for(const lang in oldValue.value?.title ?? {}) {
                    Data.value.title[lang] = oldValue.value.title[lang]
                }
    
                for(const lang in oldValue.value?.slug ?? {}) {
                    Data.value.slug[lang] = oldValue.value.slug[lang]
                }
    
                hidden()
            }

            const refesh = () => {
                store.dispatch('files-folder/getFolders', {
                    parent: parent.value,
                    order_by:'ordering_count ASC'
                })
                    .then(response => {
                        const res = response.data.data
                        folders.value = res
                })

                model.value = null
                images.value = []
                selectedCoverImage.value = null
                selectedImages.value = []
            }
    
            const getData = async function(data){
                store.dispatch('files-folder/getFolders', {
                    parent: parent.value,
                    order_by:'ordering_count ASC'
                })
                    .then(response => {
                        const res = response.data.data
                        folders.value = res
                })
    
                if(data) {
                    data.is_active = data.is_active? true: false
                    Data.value = {
                        ...data,
                        amenities : Array.isArray(data.amenities)?data.amenities.map((g) => {
                           return {
                                title: g.title[this.currentLocale],
                                id: g.id, 
                           }
                        })
                        : []
                    }
    
                    oldValue.value = JSON.parse(JSON.stringify({...data}))
    
                    model.value = null
                    images.value = []
                    selectedCoverImage.value = null
                    selectedImages.value = []
    
                    if(data.files.length){
                        const files = data.files
                        const folderId = files[0].folder_id
                        
                        store.dispatch('files-folder/getFolderById', folderId)
                            .then(response => {
                                const res = response.data.data
                                images.value = res[0].images
    
                                model.value = folderId
                                for(const file of files){
                                    if(file.is_feature){
                                        selectedCoverImage.value = file
                                    } else {
                                        selectedImages.value.push(file)
                                    }
                                }
                        })
                    }
                } else {
                    model.value = null
                    images.value = []
                    selectedCoverImage.value = null
                    selectedImages.value = []
    
                    Data.value.slug = {}
                    Data.value.url = null
                    Data.value.title = {}
                    Data.value.description = {}
                    Data.value.amenities = []
                    Data.value.meta_title = ''
                    Data.value.meta_description = ''
                    Data.value.meta_keyword = ''
                    Data.value.sub_title = {}
                    Data.value.short_description = {}
                    Data.value.price = 0
                    Data.value.is_active = true
                }
            }
    
            const getLangs = (filter) => {
                store.dispatch("app-languages/fetchData", filter).then((res) => {
                    if (res.data.success) {
                    langs.value = res.data.data;
                    }
                });
            };
    
            let filter = {
                is_active:1,
            }
    
            const checkPrice = (data) => {
                const regExp = /^[+-]?\d+(\.\d+)?$/
                const checkPrice = regExp.test(data)
                return checkPrice
            }
    
            const onSubmit = async () => {
                if (formObserver.value) {
                const isValid = await formObserver.value.validate();

                if (!isValid) {
                const errorMessages = Object.entries(formObserver.value.errors)
                    .filter(([key, message]) => message.length)
                    .map(([key, message]) => `${message}`)

                toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                    title: `Error`,
                    icon: 'BellIcon',
                    variant: 'danger',
                    text: `${errorMessages.join(" , ")}`,
                    },
                });

                return
                }
            }

                const validatePrice = checkPrice(Data.value.price)
    
                if(!validatePrice){
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: "Error Room Price",
                            icon: 'AlertTriangleIcon',
                            variant: 'danger',
                            text: `price should be number`,
                        },
                    });
    
                    return
                }
    
                Data.value.is_active = Data.value.is_active?1:0;
                
                if(!Data.value.id){
                    Data.value.cover_image = selectedCoverImage.value
                    Data.value.files = selectedImages.value
    
                    store.dispatch('app-room/addRoom', Data.value)
                    .then((res) => {
                        toast({
                            component: ToastificationContent,
                            props: {
                                title: `Success`,
                                icon: 'BellIcon',
                                variant: 'success',
                                text: `${res.data.message}`,
                            },
                        });
    
                        emit('refetch-data')
                        emit('update:is-room-form-sidebar-active', false)
                    })
                    .catch((error) => {
                        toast({
                            component: ToastificationContent,
                            props: {
                                title: "Error Add Room",
                                icon: 'AlertTriangleIcon',
                                variant: 'danger',
                                text: error.response.data.message,
                            },
                        })
                    })
                } else {
    
                    Data.value.cover_image = selectedCoverImage.value
    
                    if(!Data.value.cover_image){
                        Data.value.files = []
                    } else {
                        Data.value.files = selectedImages.value
                    }
                    
                    store.dispatch('app-room/updateRoom', Data.value)
                    .then((res) => {
                        toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Success`,
                                icon: 'BellIcon',
                                variant: 'success',
                                text: `${res.data.message}`,
                            },
                        });
    
                        emit('refetch-data')
                        emit('update:is-room-form-sidebar-active', false)
                    })
                    .catch((error) => {
                        toast({
                            component: ToastificationContent,
                            props: {
                                title: "Error Update Room",
                                icon: 'AlertTriangleIcon',
                                variant: 'danger',
                                text: error.response.data.message,
                            },
                        })
                    })
                }
            }

            const resetForm = () => {
                Data.value = blankData
            }
    
            const {
                getValidationState,
                refFormObserver
            } = formValidation(resetData);
    
            const refeshList = async () => {
                emit("refetch-data");
            };
    
            getLangs(filter);
    
            return {
                langs,
                getData,
                resetData,
                resetForm,
                getValidationState,
                refFormObserver,
                tabIndex,
                Data,
                amenitiesData,
                onSubmit,
                changeTitle,
                refeshList,
                folders,
                eventSelected,
                images,
                selectedCoverImage,
                selectedRoomImages,
                eventImgSelected,
                selectedImages,
                eventSelectedCoverImg,
                selectedCoverImage,
                model,
                uploadImage,
                isModalFileListsShow,
                oldValue,
                close,
                refesh,
                formObserver,
                uploadType,
            }
        }
    }
</script>
    
<style lang="scss">
    @import "@core/scss/vue/libs/quill.scss";

    .selected-img{
        border: solid 5px #FFB341;
    }

    .disable-img{
        cursor: not-allowed;
        opacity: 0.5;
    }
</style>